import type { IWebhookEventDef, IWebhookGroup, IWebhookItemType } from "../constants/webhook-catalog";
import { ValidationUtility } from "../validation-utility";
import { IValidatorType } from "../validator-interface";

/**
 * Webhooks v2 endpoint management (several endpoints per community). Community admin:
 * communityId comes from the community token. Platform admin (`IPlatform*` / `VPlatform*`):
 * the same shapes plus `communityId` in the body (POST/PUT/DELETE) or query (GET).
 */

export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'held' | 'expired' | 'skipped';
export const WEBHOOK_DELIVERY_STATUSES: readonly WebhookDeliveryStatus[] = ['pending', 'delivered', 'failed', 'held', 'expired', 'skipped'];

export interface IWebhookFilters { spaceIds?: string[]; itemTypes?: string[] }

/** Plain object with optional string-array `spaceIds` / `itemTypes`; nothing else. */
export function validateWebhookFilters(value: any): IWebhookFilters | undefined {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
	const out: IWebhookFilters = {};
	for (const key of Object.keys(value)) {
		if (key !== 'spaceIds' && key !== 'itemTypes') return undefined;
		const list = value[key];
		if (list == null) continue;
		if (!Array.isArray(list) || list.length > 100 || list.some((v) => typeof v !== 'string' || !v || v.length > 100)) return undefined;
		out[key] = list;
	}
	return out;
}

const validateStringArray = (value: any, max: number) =>
	Array.isArray(value) && value.length <= max && value.every((v) => typeof v === 'string' && v.length <= 100) ? value : undefined;
const validateIntArray = (value: any, max: number) =>
	Array.isArray(value) && value.length <= max && value.every((v) => Number.isInteger(Number(v))) ? value.map(Number) : undefined;
const validateDeliveryStatus = (value: any) => (WEBHOOK_DELIVERY_STATUSES as readonly string[]).includes(value) ? value : undefined;
const validateMs = (value: any) => {
	const n = Number(value);
	return Number.isFinite(n) && n >= 0 ? n : undefined;
};

export interface IWebhookEndpoint {
	id: string;
	name: string | null;
	url: string;
	status: 'active' | 'disabled';
	disabledReason: string | null;   // 'gone' | 'failures' | 'admin'
	disabledAt: number | null;
	subscribedEvents: string[];
	subscribedGroups: number[];
	filters: IWebhookFilters | null;
	secret: string;
	secretRotatedAt: number | null;
	apiVersion: string;
	createdAt: number;
	lastDelivery: { status: WebhookDeliveryStatus; httpStatus: number | null; createdAt: number } | null;
}

//Req GET /admin/community/external-vendor/webhooks
export interface IWebhookListRequest { }
//Res GET /admin/community/external-vendor/webhooks
export interface IWebhookListResponse { items: IWebhookEndpoint[]; maxEndpoints: number }

//Req POST /admin/community/external-vendor/webhooks
export interface IWebhookCreateRequest {
	name: string;
	url: string;
	subscribedEvents?: string[];
	subscribedGroups?: number[];
	filters?: IWebhookFilters;
}
//Val POST /admin/community/external-vendor/webhooks
export const VWebhookCreate: IValidatorType<IWebhookCreateRequest> = {
	name: { function: ValidationUtility.validateStringFields, params: [1, 100], required: true },
	url: { function: ValidationUtility.validateUrl, params: [], required: true },
	subscribedEvents: { function: validateStringArray, params: [200], required: false },
	subscribedGroups: { function: validateIntArray, params: [50], required: false },
	filters: { function: validateWebhookFilters, params: [], required: false },
};
//Res POST /admin/community/external-vendor/webhooks
export interface IWebhookCreateResponse { id: string; secret: string }

//Req PUT /admin/community/external-vendor/webhooks
export interface IWebhookUpdateRequest {
	id: string;
	name?: string;
	url?: string;
	subscribedEvents?: string[];
	subscribedGroups?: number[];
	filters?: IWebhookFilters;
	enabled?: boolean;
}
//Val PUT /admin/community/external-vendor/webhooks
export const VWebhookUpdate: IValidatorType<IWebhookUpdateRequest> = {
	id: { function: ValidationUtility.validateEntityId, params: [], required: true },
	name: { function: ValidationUtility.validateStringFields, params: [1, 100], required: false },
	url: { function: ValidationUtility.validateUrl, params: [], required: false },
	subscribedEvents: { function: validateStringArray, params: [200], required: false },
	subscribedGroups: { function: validateIntArray, params: [50], required: false },
	filters: { function: validateWebhookFilters, params: [], required: false },
	enabled: { function: ValidationUtility.validateBoolean, params: [], required: false },
};
//Res PUT /admin/community/external-vendor/webhooks
export type IWebhookUpdateResponse = IWebhookEndpoint;

//Req DELETE /admin/community/external-vendor/webhooks
export interface IWebhookIdRequest { id: string }
//Val DELETE /admin/community/external-vendor/webhooks (also POST .../secret, GET .../delivery)
export const VWebhookId: IValidatorType<IWebhookIdRequest> = {
	id: { function: ValidationUtility.validateEntityId, params: [], required: true },
};
//Res DELETE /admin/community/external-vendor/webhooks
export interface IWebhookDeleteResponse { }

//Req POST /admin/community/external-vendor/webhooks/secret  (IWebhookIdRequest, VWebhookId)
//Res POST /admin/community/external-vendor/webhooks/secret  (shown once; the previous secret keeps signing for 24 h)
export interface IWebhookRollSecretResponse { secret: string }

//Req POST /admin/community/external-vendor/webhooks/test
export interface IWebhookTestSendRequest { id: string; event: string }
//Val POST /admin/community/external-vendor/webhooks/test
export const VWebhookTestSend: IValidatorType<IWebhookTestSendRequest> = {
	id: { function: ValidationUtility.validateEntityId, params: [], required: true },
	event: { function: ValidationUtility.validateStringFields, params: [1, 100], required: true },
};
//Res POST /admin/community/external-vendor/webhooks/test
export interface IWebhookTestSendResponse { deliveryId: string }

//Req GET /admin/community/external-vendor/webhooks/deliveries
export interface IWebhookDeliveriesRequest {
	id: string;                 // endpoint (subscription) id
	status?: WebhookDeliveryStatus;
	event?: string;
	from?: number;              // ms, inclusive
	to?: number;                // ms, inclusive
	cursor?: string;            // `next` from the previous page
}
//Val GET /admin/community/external-vendor/webhooks/deliveries
export const VWebhookDeliveries: IValidatorType<IWebhookDeliveriesRequest> = {
	id: { function: ValidationUtility.validateEntityId, params: [], required: true },
	status: { function: validateDeliveryStatus, params: [], required: false },
	event: { function: ValidationUtility.validateStringFields, params: [1, 100], required: false },
	from: { function: validateMs, params: [], required: false },
	to: { function: validateMs, params: [], required: false },
	cursor: { function: ValidationUtility.validateEntityId, params: [], required: false },
};
export interface IWebhookDeliveryItem {
	id: string;
	event: string;
	code: number;
	createdAt: number;
	status: WebhookDeliveryStatus;
	httpStatus: number | null;
	durationMs: number | null;
	attempt: number;
	isTest: boolean;
}
//Res GET /admin/community/external-vendor/webhooks/deliveries
export interface IWebhookDeliveriesResponse { items: IWebhookDeliveryItem[]; next: string | null }

//Req GET /admin/community/external-vendor/webhooks/delivery  (IWebhookIdRequest, VWebhookId; id = delivery id)
//Res GET /admin/community/external-vendor/webhooks/delivery
export interface IWebhookDeliveryDetailResponse extends IWebhookDeliveryItem {
	subscriptionId: string;
	eventId: string;            // public "evt_<uuid>"
	deliveredAt: number | null;
	nextAttemptAt: number | null;
	error: string | null;
	body: Record<string, unknown>;
	responseSnippet: string | null;
}

//Req POST /admin/community/external-vendor/webhooks/resend
/** One delivery (`id`), or every failed delivery of one endpoint (`subscriptionId` + `status: 'failed'`, optional range; 500 per call). */
export interface IWebhookResendRequest {
	id?: string;
	subscriptionId?: string;
	status?: 'failed';
	from?: number;
	to?: number;
}
//Val POST /admin/community/external-vendor/webhooks/resend
export const VWebhookResend: IValidatorType<IWebhookResendRequest> = {
	id: { function: ValidationUtility.validateEntityId, params: [], required: false },
	subscriptionId: { function: ValidationUtility.validateEntityId, params: [], required: false },
	status: { function: (v: any) => (v === 'failed' ? v : undefined), params: [], required: false },
	from: { function: validateMs, params: [], required: false },
	to: { function: validateMs, params: [], required: false },
};
//Res POST /admin/community/external-vendor/webhooks/resend
export interface IWebhookResendResponse { queued: number }

//Req GET /admin/community/external-vendor/webhooks/catalog
export interface IWebhookCatalogRequest { }
//Res GET /admin/community/external-vendor/webhooks/catalog
export interface IWebhookCatalogResponse {
	groups: readonly IWebhookGroup[];
	events: readonly IWebhookEventDef[];
	/** The itemType values emitters send, per group (for the endpoint item-type filter). */
	itemTypes: readonly IWebhookItemType[];
	version: string;
	tokensPerDelivery: number;   // from the active price book; 0 until WebhookDelivered is priced
	/** Platform-admin mirror only (needs communityId): the community's direct spaces, for the spaceIds filter. */
	spaces?: readonly { id: string; name: string }[];
}

// ── Platform-admin mirror: same shapes plus communityId ───────────────────────────
const VCommunityId = {
	communityId: { function: ValidationUtility.validateObjectId, params: [], required: true },
};
export type WithCommunityId<T> = T & { communityId: string };
export const VPlatformWebhookCommunity: IValidatorType<{ communityId: string }> = VCommunityId;
export const VPlatformWebhookCreate: IValidatorType<WithCommunityId<IWebhookCreateRequest>> = { ...VWebhookCreate, ...VCommunityId };
export const VPlatformWebhookUpdate: IValidatorType<WithCommunityId<IWebhookUpdateRequest>> = { ...VWebhookUpdate, ...VCommunityId };
export const VPlatformWebhookId: IValidatorType<WithCommunityId<IWebhookIdRequest>> = { ...VWebhookId, ...VCommunityId };
export const VPlatformWebhookTestSend: IValidatorType<WithCommunityId<IWebhookTestSendRequest>> = { ...VWebhookTestSend, ...VCommunityId };
export const VPlatformWebhookDeliveries: IValidatorType<WithCommunityId<IWebhookDeliveriesRequest>> = { ...VWebhookDeliveries, ...VCommunityId };
export const VPlatformWebhookResend: IValidatorType<WithCommunityId<IWebhookResendRequest>> = { ...VWebhookResend, ...VCommunityId };
